// پیشنهادِ ثبت‌نام به مهمان — WHOSUP-308 (بازبینیِ اپل، بندِ 5.1.1(v)).
//
// اپل گفت کشف به حساب وابسته نیست، پس نباید پشتِ ورود باشد. از آن طرف،
// مهمانی که هیچ‌وقت نشنود «با حساب چه چیزی گیرت می‌آید» هیچ‌وقت عضو
// نمی‌شود. این فایل قانونِ «کِی و چطور بپرسیم» است، برای هر دو اپ یکی:
//
//   الف) «برگه»: لحظه‌ی نیاز. مهمان کاری زد که بی‌حساب نمی‌شود (پلن، ذخیره،
//        «هستم»، دوستان، …). برگه دلیلش را می‌گوید و بعد از ثبت‌نام همان
//        کار ادامه پیدا می‌کند. سقف ندارد — خودِ کاربر خواسته.
//   ب) «نوار»: یادآوریِ ملایم بر اساسِ **رفتار**، نه زمان — بعد از بازکردنِ
//        ۳ رویداد، یا روزِ دومی که برمی‌گردد. نوارِ پایینِ صفحه، نه پنجره‌ی
//        وسطِ صفحه، و همیشه «بعداً» دارد.
//   ج) سقفِ نوار: بارِ اول، ۳ روز بعد، یک هفته بعد — و تمام. بعد از سه بار
//        فقط (الف) می‌ماند.
//
// منطقِ خالص (`nudgeDue`، `afterOpen`، `afterVisit`) با `npm test` آزموده
// می‌شود؛ پایینِ فایل فقط لایه‌ی حافظه‌ی دستگاه است.
import { storage } from '../platform.ts';
import { reportError } from './report.ts';

/** چرا برگه باز شد — هر کدام یک جمله‌ی خودش را دارد. */
export type GateReason = 'plan' | 'save' | 'respond' | 'friends' | 'notifs' | 'profile' | 'follow' | 'suggest';

/** کلیدِ ترجمه‌ی جمله‌ی هر دلیل. متنِ بقیه‌ی برگه همان `JoinPitch` است. */
export const GATE_REASON_KEY: Record<GateReason, string> = {
  plan: 'gatePlan',
  save: 'gateSave',
  respond: 'gateRespond',
  friends: 'gateFriends',
  notifs: 'gateNotifs',
  profile: 'gateProfile',
  follow: 'gateFollow',
  suggest: 'gateSuggest',
};

/** سه مزیتی که زیرِ هر پیشنهاد می‌آید — عیناً همانِ `JoinPitch` (variant `after`). */
export const JOIN_BENEFIT_KEYS = ['joinWhyUpdates', 'joinWhyChat', 'joinWhyOwn'] as const;

/**
 * مزیت‌های مخصوصِ هر سربرگِ مهمان، زیرِ دکمه‌ی عضویت. عمداً جدا از
 * `JOIN_BENEFIT_KEYS`: هر صفحه از کارِ خودش حرف می‌زند، نه یک متنِ تکراری.
 */
export const PANEL_BENEFIT_KEYS: Partial<Record<GateReason, readonly string[]>> = {
  plan: ['panelPlan1', 'panelPlan2', 'panelPlan3'],
  friends: ['panelFriends1', 'panelFriends2', 'panelFriends3'],
};

const DAY =86_400_000;

/** فاصله‌ی هر نمایشِ نوار از نمایشِ قبلی. طولش همان سقف است. */
export const NUDGE_GAPS_MS = [0, 3 * DAY, 7 * DAY] as const;
export const NUDGE_MAX = NUDGE_GAPS_MS.length;
/** چند رویداد باز کند تا نوار معنی پیدا کند. */
export const NUDGE_OPENS = 3;
/** روزِ چندمی که برمی‌گردد. */
export const NUDGE_DAYS = 2;

export interface NudgeState {
  /** چند رویداد/مکان باز کرده (فقط مهمان). */
  opens: number;
  /** روزهای جدایی که اپ را باز کرده — کلیدِ روزِ محلی، مثل `2026-10-07`. */
  days: string[];
  /** زمانِ هر بار که نوار نشان داده شد. */
  shown: number[];
}

export const EMPTY_NUDGE: NudgeState = { opens: 0, days: [], shown: [] };

/** کلیدِ روزِ محلی. روزهای بیشتر از `NUDGE_DAYS` لازم نیست نگه داشته شوند. */
export const dayKey = (at: number): string => {
  const d = new Date(at);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const afterOpen = (s: NudgeState): NudgeState => ({ ...s, opens: s.opens + 1 });

export function afterVisit(s: NudgeState, at: number): NudgeState {
  const k = dayKey(at);
  if (s.days.includes(k) || s.days.length >= NUDGE_DAYS) return s;
  return { ...s, days: [...s.days, k] };
}

export const afterShown = (s: NudgeState, at: number): NudgeState => ({ ...s, shown: [...s.shown, at] });

/** چرا این بار نوار آمد — برای `app_events`، تا معلوم شود کدام راه کار کرد. */
export type NudgeTrigger = 'opens' | 'return';

/**
 * الان وقتِ نوار است؟ `null` یعنی نه.
 *
 * رفتار شرطِ **ورود** است و زمان شرطِ **تکرار**: بارِ اول به‌محضِ اینکه
 * رفتار رسید، بارِ دوم دستِ‌کم ۳ روز بعد از اولی، سوم یک هفته بعد از دومی.
 * ساعتِ عقب‌رفته (`at` کوچک‌تر از آخرین نمایش) هم «هنوز نه» است.
 */
export function nudgeDue(s: NudgeState, at: number): NudgeTrigger | null {
  const n = s.shown.length;
  if (n >= NUDGE_MAX) return null;
  const trigger: NudgeTrigger | null = s.opens >= NUDGE_OPENS ? 'opens'
    : s.days.length >= NUDGE_DAYS ? 'return' : null;
  if (!trigger) return null;
  if (n > 0 && at - s.shown[n - 1] < NUDGE_GAPS_MS[n]) return null;
  return trigger;
}

/** حافظه‌ی خراب یا دست‌کاری‌شده نباید اپ را بشکند؛ هرچه نامعتبر است صفر می‌شود. */
export function parseNudge(raw: string | null): NudgeState {
  if (!raw) return EMPTY_NUDGE;
  try {
    const v = JSON.parse(raw) as Partial<NudgeState>;
    return {
      opens: typeof v.opens === 'number' && v.opens >= 0 ? v.opens : 0,
      days: Array.isArray(v.days) ? v.days.filter((d): d is string => typeof d === 'string') : [],
      shown: Array.isArray(v.shown) ? v.shown.filter((d): d is number => typeof d === 'number') : [],
    };
  } catch {
    return EMPTY_NUDGE;
  }
}

/* ══════════════════════ حافظه‌ی دستگاه ══════════════════════ */

const KEY = 'signupNudge.v1';

async function load(): Promise<NudgeState> {
  try {
    return parseNudge(await storage().getItem(KEY));
  } catch {
    return EMPTY_NUDGE;
  }
}

async function save(s: NudgeState): Promise<void> {
  try {
    await storage().setItem(KEY, JSON.stringify(s));
  } catch (e) {
    reportError('signupNudge.save', e);
  }
}

/** مهمان یک رویداد/مکان را باز کرد. */
export async function noteGuestOpen(): Promise<void> {
  await save(afterOpen(await load()));
}

/**
 * اگر وقتش است، نوار را «نشان‌داده‌شده» ثبت می‌کند و دلیلش را برمی‌گرداند.
 *
 * ثبتِ روزِ بازدید هم همین‌جاست: هر بار که صفحه‌ی کشف برای مهمان باز
 * می‌شود این صدا زده می‌شود، و این همان «برگشتن» است.
 */
export async function takeNudge(at: number = Date.now()): Promise<NudgeTrigger | null> {
  const s = afterVisit(await load(), at);
  const due = nudgeDue(s, at);
  await save(due ? afterShown(s, at) : s);
  return due;
}
